import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  getBulkIgnoreStatus,
  markProductsIgnored,
  requestProductsCount,
  setBulkIgnoreJob,
} from "../../../actions/products";
import { setGlobalAlert } from "../../../actions/common";
import "../styles.scss";

export const BULK_IGNORE_JOB_KEY = "riverr.listingsBulkIgnoreJob";
const POLL_MS = 2000;

/**
 * App-level poller for async listing bulk-ignore jobs.
 * Keeps running across route changes so users can leave Listings safely.
 */
const BulkIgnoreJobWatcher = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const job = useSelector((state) => state?.products?.bulkIgnoreJob);
  const pollTimerRef = useRef(null);
  const finishingRef = useRef(false);
  const [floatHidden, setFloatHidden] = useState(false);
  const onListingsIndex =
    location.pathname === "/listings" || location.pathname === "/listings/";

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearStoredJob = () => {
    try {
      localStorage.removeItem(BULK_IGNORE_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  const finishJob = (status) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearPoll();
    clearStoredJob();

    const succeededIds = status?.succeededIds || [];
    if (succeededIds.length) {
      dispatch(markProductsIgnored(succeededIds, true));
      dispatch(requestProductsCount());
    }

    dispatch(setBulkIgnoreJob(status));

    const failed = status?.failed || 0;
    const succeeded = status?.succeeded || 0;
    const isHardFail =
      status?.status === "failed" || (failed > 0 && succeeded === 0);

    if (isHardFail) {
      dispatch(
        setGlobalAlert({
          type: "error",
          message: status?.error || "Bulk ignore failed. Please try again.",
        })
      );
    } else if (failed > 0) {
      dispatch(
        setGlobalAlert({
          type: "warning",
          message:
            status?.error ||
            `${succeeded} ignored, ${failed} failed. You can retry the failed ones.`,
        })
      );
    } else {
      dispatch(
        setGlobalAlert({
          type: "success",
          message: `${succeeded || status?.total || 0} listing(s) ignored.`,
        })
      );
    }

    finishingRef.current = false;
  };

  const pollJob = async (jobId) => {
    try {
      const res = await getBulkIgnoreStatus(jobId);
      const status = res?.data;
      if (!status) throw new Error("Unable to read ignore progress.");

      dispatch(setBulkIgnoreJob(status));

      if (status.status === "processing") {
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_MS);
        return;
      }

      finishJob(status);
    } catch (err) {
      clearPoll();
      clearStoredJob();
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to check ignore progress.";
      dispatch(
        setBulkIgnoreJob({
          jobId,
          status: "failed",
          error: message,
          total: 0,
          completed: 0,
          succeeded: 0,
          failed: 0,
        })
      );
      dispatch(setGlobalAlert({ type: "error", message }));
    }
  };

  // Resume from localStorage on app load / refresh.
  useEffect(() => {
    let jobId = null;
    try {
      jobId = localStorage.getItem(BULK_IGNORE_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
    if (jobId) {
      dispatch(
        setBulkIgnoreJob({
          jobId,
          status: "processing",
          total: 0,
          completed: 0,
          succeeded: 0,
          failed: 0,
        })
      );
    }
  }, [dispatch]);

  // Poll whenever we have a processing job.
  useEffect(() => {
    const jobId = job?.jobId;
    if (!jobId || job?.status !== "processing") {
      return clearPoll;
    }

    setFloatHidden(false);
    clearPoll();
    pollJob(jobId);
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.jobId, job?.status === "processing"]);

  // Listings page already shows the top progress banner.
  if (!job || job.status !== "processing" || floatHidden || onListingsIndex) {
    return null;
  }

  const total = job.total || 0;
  const completed = job.completed || 0;
  const percent =
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

  return (
    <div className="listings-bulk-ignore-float" role="status" aria-live="polite">
      <strong>Ignoring listings</strong>
      <div>
        {completed} of {total || "…"} · {percent}%
      </div>
      <div className="listings-bulk-ignore-float__track">
        <div
          className="listings-bulk-ignore-float__fill"
          style={{ width: `${percent}%` }}
        />
      </div>
      <button
        type="button"
        className="listings-bulk-ignore-float__dismiss"
        onClick={() => setFloatHidden(true)}
      >
        Hide
      </button>
    </div>
  );
};

export default BulkIgnoreJobWatcher;
